import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  profilePlanCodeIsStillValid,
  resolveUserPlanCode,
} from '@/lib/resolve-user-plan-code';

interface SubscriptionStatus {
  loading: boolean;
  currentPlan: {
    code: string;
    name: string;
    min_patients: number;
    max_patients: number | null;
    max_file_uploads: number | null;
  } | null;
  patientsCount: number;
  patientsRemaining: number;
  canAddPatients: boolean;
  progressPercentage: number;
  isFree: boolean;
}

export function useSubscriptionStatus() {
  const [status, setStatus] = useState<SubscriptionStatus>({
    loading: true,
    currentPlan: null,
    patientsCount: 0,
    patientsRemaining: 0,
    canAddPatients: true,
    progressPercentage: 0,
    isFree: false,
  });

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('current_plan_code')
        .eq('id', user.id)
        .maybeSingle();

      const resolved = await resolveUserPlanCode(user.id);
      let planCode: string | null = resolved.isPendingPayment
        ? null
        : resolved.planCode;

      if (!planCode) {
        planCode = profile?.current_plan_code || 'free';

        if (planCode && planCode !== 'free') {
          const stillValid = await profilePlanCodeIsStillValid(user.id, planCode);
          if (!stillValid) {
            planCode = 'free';
          }
        }
      } else {
        planCode = planCode || 'free';
      }

      // Buscar plano atual
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('code, name, min_patients, max_patients, max_file_uploads')
        .eq('code', planCode)
        .single();

      // Contar pacientes
      const { count } = await supabase
        .from('patients_onboarding')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('status', 'active');

      const patientsCount = count || 0;
      const maxPatients = plan?.max_patients || 10; // Default free
      const patientsRemaining = Math.max(0, maxPatients - patientsCount);
      const canAddPatients = plan?.max_patients === null || patientsCount < maxPatients;
      const progressPercentage = plan?.max_patients 
        ? Math.round((patientsCount / maxPatients) * 100)
        : 0;

      setStatus({
        loading: false,
        currentPlan: plan,
        patientsCount,
        patientsRemaining,
        canAddPatients,
        progressPercentage,
        isFree: planCode === 'free',
      });
    } catch (error) {
      console.error('Error loading subscription status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }

  return { ...status, refresh: loadStatus };
}
